/**
 * WU-05: サイドバー nav の 3 グループ定義（純関数 + 型付き定数）。
 * WU-04: runtime-config の ui.enabledScreens（CH_UI_ENABLED_SCREENS）による
 * 画面セット filter もここで解決する。nav 隠しは UX であり、権限境界は
 * サーバー側 403（WU-02/WU-03）が担う。
 */
import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  ClipboardList,
  FileText,
  Filter,
  FolderTree,
  Home,
  Inbox,
  LayoutDashboard,
  Link2,
  Map,
  PhoneCall,
  Presentation,
  RadioTower,
  ScrollText,
  Settings,
  Shield,
  TrendingUp,
  UserPlus,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

export type ScreenId =
  | 'dashboard'
  | 'team-pulse'
  | 'weekly-status'
  | 'meeting-center'
  | 'evidence-history'
  | 'gift-operations'
  | 'summary-overview'
  | 'summary-calls'
  | 'summary-ai-sdr'
  | 'summary-ax'
  | 'box'
  | 'adapters'
  | 'acl'
  | 'access-management'
  | 'proposals'
  | 'kpi'
  | 'funnel'
  | 'mastery'
  | 'monitoring'
  | 'onboarding'
  | 'memory-explore'
  | 'admin'
  | 'benchmark-curation'
  | 'j-locomo-curation'
  | 'workspaces'
  | 'stats'

export interface NavItem {
  screen: ScreenId
  name: string
  href: string
  icon: LucideIcon
}

export interface NavGroup {
  label: string
  items: readonly NavItem[]
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'サマリー',
    items: [
      { screen: 'summary-overview', name: '全体', href: '/summary', icon: Home },
      { screen: 'summary-calls', name: 'コール', href: '/summary/calls', icon: PhoneCall },
      { screen: 'summary-ai-sdr', name: 'AI-SDR', href: '/summary/ai-sdr', icon: Bot },
      { screen: 'summary-ax', name: 'AX', href: '/summary/ax', icon: Workflow },
    ],
  },
  {
    label: 'MCP',
    items: [
      { screen: 'gift-operations', name: 'MCP・連携管理', href: '/gift-operations', icon: RadioTower },
    ],
  },
  {
    label: 'その他',
    items: [
      { screen: 'weekly-status', name: '現在の状況', href: '/weekly-status', icon: LayoutDashboard },
      { screen: 'meeting-center', name: '週次会議', href: '/meeting-center', icon: Presentation },
      { screen: 'evidence-history', name: '根拠・履歴', href: '/evidence-history', icon: ScrollText },
      { screen: 'team-pulse', name: 'チーム状況', href: '/team-pulse', icon: Users },
      { screen: 'funnel', name: 'ファネル', href: '/funnel', icon: Filter },
      { screen: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { screen: 'kpi', name: 'KPI', href: '/kpi', icon: BarChart3 },
      { screen: 'mastery', name: 'Mastery Map', href: '/mastery', icon: Map },
      { screen: 'proposals', name: 'Proposals', href: '/proposals', icon: ClipboardList },
      { screen: 'adapters', name: 'Adapters', href: '/adapters', icon: Link2 },
      { screen: 'acl', name: 'ACL', href: '/acl', icon: Shield },
      { screen: 'workspaces', name: 'Workspaces', href: '/workspaces', icon: Building2 },
      { screen: 'onboarding', name: 'Onboarding', href: '/onboarding', icon: UserPlus },
      { screen: 'monitoring', name: 'Monitoring', href: '/monitoring', icon: Activity },
      { screen: 'box', name: 'BOX', href: '/box', icon: Inbox },
      { screen: 'memory-explore', name: 'Memory Explore', href: '/memory-explore', icon: FolderTree },
      { screen: 'admin', name: 'Admin', href: '/admin', icon: Settings },
      { screen: 'stats', name: 'Stats', href: '/stats', icon: TrendingUp },
      { screen: 'benchmark-curation', name: 'Benchmark Curation', href: '/benchmark-curation', icon: FileText },
      { screen: 'j-locomo-curation', name: 'J-LoCoMo Curation', href: '/j-locomo-curation', icon: FileText },
    ],
  },
]

/** route は持つが nav には出さない画面（明示 except リスト） */
export const NAV_HIDDEN_SCREENS: readonly ScreenId[] = ['access-management']

/** route を持つ全画面 = nav グループ ∪ nav 非表示 except リスト */
export const ALL_SCREEN_IDS: readonly ScreenId[] = [
  ...NAV_GROUPS.flatMap((group) => group.items.map((item) => item.screen)),
  ...NAV_HIDDEN_SCREENS,
]

export type EnabledScreens = ReadonlySet<string> | null

/** enabledScreens 未設定（null）は全画面有効 = 既存挙動 */
export function isScreenEnabled(screen: ScreenId, enabled: EnabledScreens): boolean {
  return enabled === null || enabled.has(screen)
}

export function filterNavGroups(enabled: EnabledScreens): readonly NavGroup[] {
  if (enabled === null) {
    return NAV_GROUPS
  }
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => isScreenEnabled(item.screen, enabled)),
  })).filter((group) => group.items.length > 0)
}

/**
 * 無効画面への直接 URL アクセス時の redirect 先。
 * dashboard 無効時は nav 定義順で最初に有効な画面へ、全滅時は / に落とす。
 */
/**
 * nav に出せる画面が 1 つでも有効か。false のとき描画側は route を張らず
 * 「表示可能な画面がありません」の静的画面を出す（catch-all Navigate の
 * 無限 redirect ループ防止。2026-07-05 code-review 指摘）。
 */
export function hasAnyNavScreen(enabled: EnabledScreens): boolean {
  return NAV_GROUPS.some((group) =>
    group.items.some((item) => isScreenEnabled(item.screen, enabled)),
  )
}

export function defaultHref(enabled: EnabledScreens): string {
  const first = NAV_GROUPS.flatMap((group) => group.items).find((item) =>
    isScreenEnabled(item.screen, enabled),
  )
  return first?.href ?? '/'
}

/**
 * 全 route 画面（nav 表示 + 非表示 except リスト）の screen id → href 対応。
 * `Map` は lucide-react の Mastery Map アイコンと同名のため `globalThis.Map` を使う。
 */
const SCREEN_HREFS: ReadonlyMap<string, string> = new globalThis.Map([
  ...NAV_GROUPS.flatMap((group) => group.items.map((item) => [item.screen, item.href] as const)),
  ['access-management', '/access-management'],
])

/**
 * WU-08b: ui.defaultScreen（CH_UI_DEFAULT_SCREEN）の landing 解決（純関数）。
 * - 未設定 / 空白のみ → null（現行挙動維持: "/" は DashboardPage）
 * - 存在しない screen id → null（fail-safe。白画面にしない）
 * - enabledScreens/capability の積で無効化された画面 → null（fail-safe）
 * - 有効な screen id → その href。dashboard の href は永続 alias "/dashboard"
 *   （review fix: dashboard の href が "/" 一本だと、他画面を landing に設定した
 *   deploy で "/" が常に landing に奪われ Dashboard への到達経路が消える
 *   死にリンクになるため、"/" とは独立した固定 path を持たせる）
 */
export function resolveDefaultLandingHref(
  rawDefaultScreen: string | undefined,
  enabled: EnabledScreens,
): string | null {
  const trimmed = rawDefaultScreen?.trim()
  if (!trimmed) {
    return null
  }
  const href = SCREEN_HREFS.get(trimmed)
  if (href === undefined) {
    return null
  }
  if (!isScreenEnabled(trimmed as ScreenId, enabled)) {
    return null
  }
  return href
}
